import React, {
  ReactChild,
  ReactComponentElement,
  ReactSVGElement,
  useEffect,
  useRef,
  useState,
} from "react"
import styled, { css } from "styled-components"
import { fonts, colors } from "../components/Styles"
import { bp } from "../components/Styles"
import {
  ContainerOptions,
  ContentMobile,
  SwipeContainer,
} from "./layout-components"
import { useBreakpoint } from "gatsby-plugin-breakpoints"
import { useLottie, Lottie } from "react-lottie-hook"
import useComponentSize from "@rehooks/component-size"
import { useTranslation } from "gatsby-plugin-react-i18next"
import { ParallaxProvider, Parallax } from "react-scroll-parallax"
import FadeInSection from "../util/fade-in-section"
import { GatsbyImage, getImage, StaticImage } from "gatsby-plugin-image"
import { ReactSVG } from "react"

const InfoMenuContainer = styled.div`
  display: flex;
  max-width: 100%;
  flex-direction: row;
`

const InfoMenuContent = styled.div`
  flex: 0 0 80%;
  height: 37.7rem;
  position: relative;
  background: white;
`

const InfoMenuChild = styled.div<{ selected?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  opacity: 0;
  transition: opacity 0.2s linear;

  ${props =>
    props.selected &&
    css`
      opacity: 1;
    `}
`

const InfoMenuCard = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  background-color: ${colors.backgroundDarker};
  padding: 5.1rem 7.2rem;
  max-width: 100%;
  height: 100%;
  min-height: 40rem;
  background-repeat: no-repeat;
  background-position: left bottom;
  background-size: cover;
  overflow: hidden;
  box-shadow: 0px 0px 20px #00000030;
  display: flex;
  gap: 3em;
  align-items: center;

  > div h3 {
    font-size: 3.23rem;
    font-family: ${fonts.bold};
    margin-bottom: 1.2rem;
    line-height: 3.5rem;
    max-width: 100%;
    color: ${colors.primary};
  }

  > div p {
    margin-top: 1.4rem;
    font-size: 1.4rem;
    line-height: 2.7rem;
    max-width: 100%;
    color: ${colors.secondary};
    word-break: break-word;
  }

  .lottie-sizer {
    height: 100%;
  }

  .svg-icon {
    width: 100%;
    max-width: 45%;
  }
  ${bp("mobile")`
    display:block;
    position: inherit;
    padding: 1.8rem 1.8rem;
    width: 27rem;
    margin: 20px 0px;

    > div h3 {
      font-size: 1.7rem;
      line-height: 1.6;
    }

    >.svg-icon {
      width: 100%;
      max-width: 100%;
      text-align: center;
      height: 15rem;
      svg {
        max-width: 50%;
      }
    }

    > div p {
      margin-top: 1.1rem;
      font-size: 1.1rem;
      line-height: 1.7;
      width: 100%;
      max-width: 100%;
    }
  `}
`

const InfoMenuLabel = styled.button<{ isSelected: boolean }>`
  overflow: hidden;
  cursor: pointer;
  font-size: 1.43rem;
  line-height: 1.6;
  max-width: 100%;
  text-align: left;
  color: ${colors.blue};
  opacity: 0.6;
  font-family: ${fonts.light};
  opacity: 1;
  transition: all 0.4s;
  transition-property: transform, flex;
  flex: 0 0 auto;
  width: 22rem;
  padding: 0;

  ${props =>
    props.isSelected &&
    css`
      transform: scaleY(0);
      flex: 0;
    `}
`

const InfoMenuNavigation = styled.div`
  flex: 0 0 30%;
  overflow: hidden;
`

const InfoMenuButton = styled.div<{
  isSelected: boolean
}>`
  max-width: 100%;
  display: flex;
  height: 8.24rem;
  align-items: center;
  flex-direction: row;
  flex-wrap: nowrap;
  position: relative;
  color: #999999;
  cursor: pointer;

  ::before {
    content: "";
    height: 2px;
    transition: all 0.5s;
    transition-property: width, flex;
    background-color: ${colors.primary};
    border-radius: 2px;

    ${props => {
      if (!props.isSelected)
        return css`
          width: 0px;
        `
    }}
    ${props => {
      if (props.isSelected)
        return css`
          flex: 1 1 4rem;
        `
    }}
  }

  :hover {
    ::before {
      ${props => {
        if (!props.isSelected)
          return css`
            width: 4rem;
            margin-right: 1.2em;
          `
      }}
    }
  }
`

const InfoContentContainer = styled.div<{ qprem: boolean }>`
  margin-top: 10rem;
  margin-bottom: 1.5rem;
  max-width: 100%;

  > h2 {
    padding-bottom: 5.65rem;
    text-align: left;
    margin: 0rem;
    line-height: 4.2rem;
    margin-top: 2rem;
    font-size: 3.75rem;
    color: ${colors.primary};
    font-family: ${fonts.bold};
  }

  ${bp("mobile")`
  margin-top: 0;
  transform: translate3d(0, 9.8rem, 0);
  background: linear-gradient(
    0deg, ${colors.primary} 55%, rgb(0 0 0 / 0%) 50%);
        padding-bottom: 5em;
      > h2 {
        padding-bottom: 3rem;
        font-size: 2.25rem;
        text-align: center;
      }
  `}


  ${({ qprem }) =>
    qprem &&
    css`
      ${InfoMenuCard} {
        background-color: rgb(255, 255, 255);
        padding: 5.1rem 7.2rem;
        max-width: 100%;
        background-repeat: no-repeat;
        background-position: left bottom;
        background-size: cover;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: rgba(0, 0, 0, 0.19) 0px 0px 20px;

        p {
          color: ${colors.black};
        }

        ${bp("mobile")`
          padding: 2em;
          .lottie-sizer {
            height: 20em;
          }
        `}
      }
    `}};


`

interface InfoItemParams {
  label: string
  text: string
  height?: string
  width?: string
  selected?: boolean
  isMobile?: boolean
  isMobileActive?: boolean
  anim?: any
  icon?: any
}

export const InfoMenuItem: React.FC<InfoItemParams> = ({
  label,
  text,
  selected,
  isMobile,
  isMobileActive,
  anim,
  icon,
}) => {
  const sizerRef = useRef<HTMLDivElement>(null)
  const { width: sizerWidth, height: sizerHeight } = useComponentSize(sizerRef)
  const [lottieRef, , controls] = useLottie({
    loop: false,
    autoplay: (isMobile && isMobileActive) || (!isMobile && selected),
    animationData: anim,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid",
    },
  })

  const height = isMobile ? sizerWidth * (100 / 100) : sizerHeight
  const width = isMobile ? sizerWidth : sizerHeight * (100 / 100)

  useEffect(() => {
    if ((!isMobile && !selected) || (isMobile && !isMobileActive))
      controls.goToAndStop(0)
    if ((!isMobile && selected) || (isMobile && isMobileActive)) controls.play()
  }, [selected, isMobile, isMobileActive])

  return (
    <InfoMenuCard>
      {anim && (
        <div className="lottie-sizer" ref={sizerRef}>
          <Lottie style={{ margin: 0 }} {...{ lottieRef, width, height }} />
        </div>
      )}
      {icon && <div className="svg-icon">{icon}</div>}
      <div>
        <h3>{label}</h3>

        <p>{text}</p>
      </div>
    </InfoMenuCard>
  )
}

export interface InfoItem {
  label: string
  text: string
  anim?: any
  icon?: any
}

export interface InfoMenuProps {
  items: InfoItem[]
  hideHeader?: boolean
  header?: string
  id: string
  qprem?: boolean
}

const InfoMenu: React.FC<InfoMenuProps & ContainerOptions> = ({
  items,
  hideHeader,
  header,
  marginBefore,
  marginAfter,
  overlapBefore,
  overlapAfter,
  qprem,
  id,
}) => {
  const [selected, setSelected] = useState(0)
  const isMobile = useBreakpoint().mobile
  const [activeChild, setActiveChild] = useState(0)
  const { t } = useTranslation()

  const children = items.map(({ label, text, anim, icon }, key) => (
    <InfoMenuItem
      {...{ label, text, key, isMobile, anim, icon }}
      isMobileActive={activeChild === key}
    />
  ))

  if (isMobile) {
    return (
      <ParallaxProvider>
        <FadeInSection speed={0.1}>
          <ContentMobile {...{ marginAfter, overlapBefore, id, overlapAfter }}>
            <InfoContentContainer qprem={qprem ?? false}>
              {!hideHeader && (
                <h2>{header || t("Der Prozess auf einen Blick")}</h2>
              )}
              <Parallax y={[20, -10]}>
                <SwipeContainer center onSwitchActive={a => setActiveChild(a)}>
                  {children}
                </SwipeContainer>
              </Parallax>
            </InfoContentContainer>
          </ContentMobile>
        </FadeInSection>
      </ParallaxProvider>
    )
  }

  return (
    <ContentMobile
      {...{ marginBefore, marginAfter, overlapBefore, overlapAfter, id }}
    >
      <InfoContentContainer qprem={qprem ?? false}>
        {!hideHeader && <h2>{header || t("Der Prozess auf einen Blick")}</h2>}

        <InfoMenuContainer>
          <InfoMenuNavigation>
            {children.map((c, idx) => (
              <InfoMenuButton
                onClick={() => setSelected(idx)}
                key={idx}
                isSelected={selected === idx}
              >
                <InfoMenuLabel isSelected={selected === idx}>
                  {c.props.label}
                </InfoMenuLabel>
              </InfoMenuButton>
            ))}
          </InfoMenuNavigation>

          <InfoMenuContent>
            {items.map(({ label, text, anim, icon }, key) => (
              <InfoMenuChild selected={selected === key} {...{ key }}>
                <InfoMenuItem
                  {...{ anim, label, text, key, icon }}
                  selected={selected === key}
                />
              </InfoMenuChild>
            ))}
          </InfoMenuContent>
        </InfoMenuContainer>
      </InfoContentContainer>
    </ContentMobile>
  )
}

export default InfoMenu
